import { useCallback, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { collections } from '@/schema'
import { userQuery } from '@/state/users/user'

import { type SupportRequestAgentDocument, type SupportRequestDocument } from './types'

export const useSupportReads = () => {
  const { uid } = useAuthenticatedFirebaseUser().firebaseUser
  const { data: user } = useQuery(userQuery(uid))
  const markedReadAt = useRef(new Map<number, number>())

  const lastReadAt = useCallback(
    (number: number) => user?.data?.supportReads?.[String(number)]?.lastReadAt ?? null,
    [user]
  )

  const isUnreadByRequester = useCallback(
    ({ number, lastAgentReplyAt }: Pick<SupportRequestDocument, 'number' | 'lastAgentReplyAt'>) => {
      // Before users/{uid} lands, an absent cursor means the document is loading, not unread.
      if (!user || !lastAgentReplyAt) {
        return false
      }
      const cursor = lastReadAt(number)
      return !cursor || lastAgentReplyAt > cursor
    },
    [user, lastReadAt]
  )

  const isUnreadByAgent = useCallback(
    ({
      number,
      lastActivityAt,
      lastActivityBy,
    }: Pick<SupportRequestAgentDocument, 'number' | 'lastActivityAt' | 'lastActivityBy'>) => {
      if (!user || lastActivityBy === uid) {
        return false
      }
      const cursor = lastReadAt(number)
      return !cursor || lastActivityAt > cursor
    },
    [user, uid, lastReadAt]
  )

  const markRead = useCallback(
    (number: number, readUpTo: Date) => {
      const stored = lastReadAt(number)?.getTime() ?? 0
      if (Math.max(stored, markedReadAt.current.get(number) ?? 0) >= readUpTo.getTime()) {
        return
      }
      markedReadAt.current.set(number, readUpTo.getTime())
      collections.users.update(uid, { [`supportReads.${number}.lastReadAt`]: readUpTo }).catch(() => {
        markedReadAt.current.delete(number)
      })
    },
    [uid, lastReadAt]
  )

  return { isLoaded: Boolean(user), lastReadAt, isUnreadByRequester, isUnreadByAgent, markRead }
}
